import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Button,
  Paper,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import QuickEditPopover from '../../components/Inventory/QuickEditPopover';
import ItemsDataGrid from '../../components/Inventory/ItemsDataGrid';
import StatusFilter from '../../components/Inventory/StatusFilter';
import { confirm } from '../../components/MenuNew/ConfirmModal';
import {
  getProductsByMenu,
  addInventoryStock,
  removeInventoryStock,
  adjustInventoryStock,
  updateProduct,
  deleteProduct,
} from '../../redux/actions/inventory/inventory';
import {
  selectProductsByMenu,
  selectCurrentBusiness,
  selectProductsTotalCount,
  selectTotalInventory,
  selectTotalCost,
  selectTotalSales,
} from '../../redux/selectors';
import { filterInventoryItems } from '../../utils/inventorySearch';

const InventoryItems = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState([
    'Out of Stock',
    'Low Stock',
    'In Stock',
  ]); // All statuses selected by default = show all items
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Reset pagination when filter changes
  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  // Get inventory data from Redux store
  const productsByMenuArray = useSelector(selectProductsByMenu);
  const currentBusiness = useSelector(selectCurrentBusiness);
  const totalCount = useSelector(selectProductsTotalCount);
  const totalInventory = useSelector(selectTotalInventory);
  const totalCost = useSelector(selectTotalCost);
  const totalSales = useSelector(selectTotalSales);

  // Fetch products from menu if menu is available
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await dispatch(getProductsByMenu());
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  // Use productsByMenu array directly
  const dataSource = productsByMenuArray;

  // Update selectedItem when productsByMenuArray changes (after stock actions)
  useEffect(() => {
    if (selectedItem && productsByMenuArray.length > 0) {
      const updatedItem = productsByMenuArray.find(
        (item) => item._id === selectedItem._id
      );
      if (updatedItem) {
        setSelectedItem(updatedItem);
      }
    }
  }, [productsByMenuArray, selectedItem]);

  const filteredRows = useMemo(() => {
    return filterInventoryItems(dataSource, search, statusFilter);
  }, [search, statusFilter, dataSource]);

  const handleEdit = (row) => {
    const sp = new URLSearchParams();
    sp.set('name', row.name);
    history.push({
      pathname: `/inventory/items/${row._id}`,
      search: sp.toString(),
    });
  };

  const refreshProducts = async () => {
    await dispatch(getProductsByMenu());
  };

  const handleDelete = async (row) => {
    const result = await confirm({
      title: t('Confirm Item Deletion'),
      message: t('Are you sure you want to delete this item?'),
      confirmText: t('delete'),
      cancelText: t('cancel'),
      background: '#E21508',
    });

    if (result === true) {
      try {
        setLoading(true);
        await dispatch(deleteProduct(row._id));
        // Refresh the list
        await refreshProducts();
      } catch (error) {
        console.error('Error deleting item:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleQuickView = (event, row) => {
    setSelectedItem(row);
    setPopoverAnchor(event.currentTarget);
  };

  const handleClosePopover = () => {
    setSelectedItem(null);
    setPopoverAnchor(null);
  };

  const handleSaveItem = async (updatedItem) => {
    try {
      setLoading(true);
      // Prepare update data with price and unitPrice at top level
      const updateData = {
        price: updatedItem.price,
      };

      // Add unitPrice at top level if present
      if (updatedItem.unitPrice !== undefined) {
        updateData.unitPrice = updatedItem.unitPrice;
      } else if (updatedItem.inventoryInfo?.unitPrice !== undefined) {
        // Fallback for backward compatibility
        updateData.unitPrice = updatedItem.inventoryInfo.unitPrice;
      }

      // Use updateProduct for price and unitPrice updates
      await dispatch(updateProduct(updatedItem._id, updateData));
      // Refresh the inventory list
      await refreshProducts();
    } catch (error) {
      console.error('Error updating item:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStockAction = async (action, item, quantity) => {
    try {
      setLoading(true);
      const inventory = item.inventoryInfo || {};

      // Only include uuid, stock, and businessUuid fields
      const requestData = {
        uuid: inventory.uuid || item.uuid || '',
        stock: Number(quantity),
        businessUuid: currentBusiness?.uuid || '',
      };

      let apiCall;
      switch (action) {
        case 'add':
          apiCall = addInventoryStock(requestData);
          break;
        case 'remove':
          apiCall = removeInventoryStock(requestData);
          break;
        case 'adjust':
          apiCall = adjustInventoryStock(requestData);
          break;
        default:
          return;
      }

      await dispatch(apiCall);
      // Refresh the inventory list
      await refreshProducts();
    } catch (error) {
      console.error(`Error ${action}ing stock:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    history.push('/inventory/items/new');
  };

  return (
    <Box sx={{ p: 2, backgroundColor: 'white', minHeight: '100vh' }}>
      {/* Summary Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {[
          { label: t('Total Count'), value: Number(totalCount).toLocaleString() },
          { label: t('Total Inventory'), value: Number(totalInventory) % 1 !== 0 ? Number(totalInventory).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : Number(totalInventory).toLocaleString() },
          {
            label: t('Total Cost'),
            value: `$${Number(totalCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          },
          {
            label: t('Total Sales Value'),
            value: `$${Number(totalSales).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          },
        ].map((stat) => (
          <Paper
            key={stat.label}
            variant="outlined"
            sx={{
              flex: 1,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {stat.label}
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {stat.value}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Search and Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
          <TextField
            size="small"
            placeholder={t('Search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <StatusFilter
            value={statusFilter}
            onChange={handleStatusFilterChange}
            disabled={loading}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleAddNew}
            startIcon={<AddIcon />}
            disabled={loading}
            sx={{ py: '7px' }}
          >
            {t('ADD NEW')}
          </Button>
        </Box>
      </Box>

      {/* Data Grid */}
      <ItemsDataGrid
        rows={filteredRows}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onQuickView={handleQuickView}
      />

      {/* Quick Edit Popover */}
      <QuickEditPopover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handleClosePopover}
        item={selectedItem}
        onSave={handleSaveItem}
        onStockAction={handleStockAction}
      />
    </Box>
  );
};

export default InventoryItems;
